import React from 'react';
import { Link } from 'react-router-dom';
import { AccountEntity, ProfileEntity, UserEntity } from '@interpals/api-web';
import { useTranslation } from 'react-i18next';

import { makeProfileURL } from 'routes';
import { getCountriesMap } from 'utils/options';
import onlineStatusOf from 'utils/online';
import Typography from 'components/ui/Typography';
import UserAvatar from 'components/UserAvatar';

import css from './UserSnippetWithAvatar.module.scss';

interface Props {
    user?: UserEntity | AccountEntity | ProfileEntity;
    avatarSize: 'L' | 'M' | 'S' | 'XS';

    // This will appear as text on the second line if infoText is not specified, otherwise as green indicator
    withOnline?: boolean;

    // This text will appear on the second line (only available for M & L sizes)
    infoText?: string;

    omitCountry?: boolean;
    customLink?: string;
}

// This corresponds to the UserSnippet/* components in Figma:
// https://www.figma.com/file/WiUofbyTbvm21XObPBhGN5/Interpals?node-id=6366%3A28011

const UserSnippetWithAvatar: React.FC<Props> = ({
    user,
    avatarSize,
    withOnline = false,
    infoText,
    customLink,
    omitCountry = false,
}) => {
    const { t } = useTranslation();
    const countriesMap = getCountriesMap();

    const countryName =
        (user?.home_country_code && countriesMap[user.home_country_code]) ||
        (user?.country_code && countriesMap[user.country_code]);

    if (!user) {
        return (
            <div className={css.userSnippet}>
                <UserAvatar user={user} size={avatarSize} />
                <div className={css.userSnippetText}>
                    <Typography ellipsis style={avatarSize === 'S' ? 'BodyMedSemibold' : 'BodyLargeSemibold'}>
                        {t('Common.DeletedUser')}
                    </Typography>
                </div>
            </div>
        );
    }

    const targetLink = customLink ?? makeProfileURL(user.username);

    const avatar = (
        <Link to={targetLink}>
            <UserAvatar user={user} size={avatarSize} withOnline={withOnline && infoText !== undefined} />
        </Link>
    );

    if (avatarSize === 'S') {
        return (
            <div className={css.userSnippet}>
                {avatar}
                <div className={css.userSnippetTextSmall}>
                    <Link to={targetLink} className={css.userSnippetNameCountry}>
                        <Typography style="BodyMedSemibold" ellipsis>
                            {user.name}
                        </Typography>
                    </Link>
                    <Typography style="BodyMedNormal" color="gray2">
                        {infoText ?? countryName}
                    </Typography>
                </div>
            </div>
        );
    }

    const nameCountrySameLine = (withOnline || infoText) && !omitCountry;

    return (
        <div className={css.userSnippet}>
            {avatar}
            <div className={css.userSnippetText}>
                <div className={css.userSnippetNameCountry}>
                    <Link to={targetLink} className={css.userSnippetName}>
                        <Typography style="BodyLargeSemibold" ellipsis>
                            {user.name}
                            {nameCountrySameLine && ','}
                        </Typography>
                    </Link>

                    {/* Country appears on same line as name only if there is a second line with online/info */}
                    {nameCountrySameLine && countryName && (
                        <Typography
                            style="BodyLargeNormal"
                            color="gray2"
                            className={css.countryName}
                            ellipsis={countryName.length > 16}
                        >
                            {countryName}
                        </Typography>
                    )}
                </div>
                {infoText === undefined && !withOnline ? (
                    <Typography color="gray2" ellipsis>
                        {countryName}
                    </Typography>
                ) : infoText !== undefined ? (
                    <Typography style="BodyMedNormal" color="gray2" ellipsis>
                        {infoText}
                    </Typography>
                ) : (
                    <Typography color={user.online ? 'green' : 'gray2'} ellipsis>
                        {onlineStatusOf(t, user)}
                    </Typography>
                )}
            </div>
        </div>
    );
};

export default React.memo(UserSnippetWithAvatar);
